/** @jsx jsx */
import { useRef, useState, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import Player from 'react-player';
import { Link } from 'react-scroll';
import useDocumentVisibility from '../hooks/useDocumentVisibility';

import Logo from './svg/Logo';

const headerCss = css`
  height: 100vh;
  position: relative;
  width: 100vw;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
`;

const organizationCss = css`
  text-align: center;
  position: relative;
  top: -48px;
  z-index: 10;
`;

const navCss = css`
  position: absolute;
  padding: 3em 0;
  letter-spacing: 0.52em;
  bottom: 0;
  z-index: 10;
  > a {
    opacity: 0.8;
    cursor: pointer;
    transition: opacity 350ms ease;
    text-transform: uppercase;
    &:hover {
      opacity: 1;
    }
  }
`;

const videoWrapperCss = css`
  pointer-events: none;
  position: fixed;
  will-change: transform;
  z-index: -1;
`;

const playerCss = (playerReady) => css`
  opacity: ${playerReady ? 1 : 0};
  transition: opacity 300ms ease;
`;

const Header = ({ video, companyName, companyActivity }) => {
  const { url } = video;

  const [videoVisible, setVideoVisible] = useState(true);
  const prevVideoVisible = useRef(true);
  const [style, setStyle] = useState({
    left: 0,
    top: 0,
    width: '100vw',
    height: '100vh',
  });

  useEffect(() => {
    const handleScroll = () => {
      const newVideoVisible = window.scrollY < window.innerHeight;
      if (prevVideoVisible.current !== newVideoVisible) {
        prevVideoVisible.current = newVideoVisible;
        setVideoVisible(newVideoVisible);
      }
    };

    const handleResize = () => {
      const { innerHeight, innerWidth } = window;
      const videoRatio = 1.7777777777777777;
      const winRatio = innerWidth / innerHeight;
      const ratio = videoRatio < winRatio ? [1, videoRatio] : [videoRatio, 1];
      const dimension = videoRatio < winRatio ? innerWidth : innerHeight;
      const width = ratio[0] * dimension;
      const height = ratio[1] * dimension;
      setStyle({
        width,
        height,
        top: (innerHeight - height) / 2,
        left: (innerWidth - width) / 2,
      });
    };

    handleResize();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [playerReady, setPlayerReady] = useState(false);
  const documentVisible = useDocumentVisibility() !== 'hidden';

  return (
    <header css={headerCss}>
      <div css={videoWrapperCss} style={style}>
        <Player
          css={playerCss(playerReady)}
          onStart={() => {
            setPlayerReady(true);
            console.log('play');
          }}
          url={url}
          playing={documentVisible && videoVisible}
          muted
          loop
          playsinline
          width="100%"
          height="100%"
        />
      </div>
      <div
        css={organizationCss}
        itemScope
        itemType="http://schema.org/Organization"
      >
        <Logo />
        <h4 itemProp="name">{companyName}</h4>
        <h5 itemProp="makesOffer" itemScope itemType="http://schema.org/Offer">
          <span itemProp="category">{companyActivity}</span>
        </h5>
      </div>
      <nav css={navCss}>
        <Link to="films" smooth duration={800}>
          Films
        </Link>
        &nbsp;/&nbsp;
        <Link to="what" smooth duration={1200}>
          What
        </Link>
        &nbsp;/&nbsp;
        <Link to="contact" smooth duration={1500}>
          Contact
        </Link>
      </nav>
    </header>
  );
};

export default Header;
